import styled from 'styled-components';
import {
  darkerBlackPearl,
  lima,
  nepal,
  razzmatazz,
} from '@paradigm/design-system/src/colors';
import { getVenue } from '@paradigm/features/src/unified-rfqs/entities/products/venue';
import {
  getLongNameWithoutVenue,
  getProduct,
} from '@paradigm/features/src/products/domain';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';
import { getProductCodesDescription } from '@paradigm/features/src/unified-rfqs/utils/productCodes';
import {
  CUSTOM_DESCRIPTION_PREFIX,
  isCustomDescription,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/rfqs-functions';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { Maybe } from '@paradigm/utils/types';
import BboViewProvider from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import { DrfqAccount } from '../drfq/DrfqAccount';
import ColoredStrategyDescription from './strategy-description/ColoredStrategyDescription';
import MarkPrice from './MarkPrice';
import { ProductInfo } from './RfqStrategySummary';

export function getSideText(side: 'BUY' | 'SELL' | null) {
  switch (side) {
    case 'BUY':
      return <Label color={lima}>Bought</Label>;
    case 'SELL':
      return <Label color={razzmatazz}>Sold</Label>;
    default:
      return <Label>Executed</Label>;
  }
}

export const MODAL_TITLES = {
  Active: 'Details',
  Closed: 'Details',
  Filled: 'Trade Recap',
  'Trd Away': 'Trade Recap',
  Canceled: 'Canceled',
  Expired: 'Expired',
  Draft: 'Details',
} as const;

interface LeftInfoProps {
  venue: string;
  productCode?: string | undefined;
  productCodes?: readonly string[];
}

export function LeftInfo({ venue, productCode, productCodes }: LeftInfoProps) {
  const venueInfo = getVenue(venue);
  const longName =
    productCodes != null && productCodes.length > 1
      ? getProductCodesDescription(productCodes)
      : getLongNameWithoutVenue(getProduct(productCode ?? '').longName);
  return (
    <>
      <p>{longName}</p>
      <p>{venueInfo.name}</p>
    </>
  );
}

interface MobileRfqInfoProps {
  description: string;
  venue: string;
  productCode: Maybe<string>;
  productCodes?: readonly string[];
}

export function MobileRfqInfo({
  description,
  venue,
  productCode,
  productCodes,
}: MobileRfqInfoProps) {
  return (
    <MobileRfqInfoContainer>
      <div css="opacity: 0.6;">
        <ProductInfo
          venue={venue}
          productCode={productCode}
          productCodes={productCodes}
        />
      </div>
      <div css="text-align: end; font-size: 1rem;">
        {isCustomDescription(description) ? (
          <>
            <div>{CUSTOM_DESCRIPTION_PREFIX}</div>
            <div>See table for details</div>
          </>
        ) : (
          description
        )}
      </div>
    </MobileRfqInfoContainer>
  );
}

export const MobileRfqInfoContainer = styled.div`
  background-color: ${darkerBlackPearl};
  padding: 1rem;
  display: inline-grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  justify-content: space-between;
  * {
    margin-bottom: 0;
  }
`;

interface Props {
  readonly drfq: Drfq;
  readonly isShowingMarkPrice?: boolean;
}

export function DrfqDetailsContent({
  drfq,
  isShowingMarkPrice = false,
}: Props) {
  return (
    <BboViewProvider>
      <Container>
        <DrfqAccount drfq={drfq} />
        <div css="margin: auto;">
          <p css="text-align: left; margin: 0 0 6px 10px ">Strategy</p>
          <DescriptionContainer>
            <TagSpan>
              <ColoredStrategyDescription>
                {drfq.description}
              </ColoredStrategyDescription>
            </TagSpan>
            {isShowingMarkPrice && <MarkPrice />}
          </DescriptionContainer>
        </div>
        <DateInfo>
          <p>{hourFormat(drfq.last_updated_at)}</p>
          <p>{shortDateFormat(drfq.last_updated_at)}</p>
        </DateInfo>
      </Container>
    </BboViewProvider>
  );
}

const DescriptionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 24ch;
  align-items: center;
`;

export const Container = styled.div`
  position: relative;
  display: flex;
  font-size: 0.8rem;
`;

export const TradeInfoGrid = styled.div`
  display: inline-grid;
  gap: 5px;
  margin: 0 auto;
  grid-template-columns: auto auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-auto-flow: column;
    align-items: center;
    gap: 0.5rem;
  }
`;

export const Label = styled.span`
  text-align: right;
  margin-top: 5px;
  color: ${({ color }) => color ?? 'inherit'};
  font-weight: ${({ color }) => (color != null ? '600' : 'normal')};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-top: 0;
    line-height: 1;
    font-size: 1rem;
    line-height: 1.2rem;
  }
`;

export const TagSpan = styled.span`
  padding: 5px 15px;
  border-radius: 5px;
  margin: 0 0.5rem;
  font-weight: bold;
  display: inline-block;
  background-color: #000;
  box-sizing: content-box;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-top: 0;
    line-height: 1;
    font-size: 1rem;
  }
`;

export const DateInfo = styled.div`
  position: absolute;
  top: 0;
  right: 0;

  p {
    text-align: right;
    color: ${nepal};
    margin: 0;
    font-size: 0.7rem;
  }
`;

export const IdsContainer = styled.div`
  opacity: 0.5;
  font-size: 0.75rem;
  line-height: 14px;
  display: flex;
  gap: 0.5rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

export const ExecutedTime = styled.span`
  font-size: 0.875rem;
  letter-spacing: 0;
  line-height: 14px;
  text-align: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
    text-align: left;
  }
`;
export const DateInfoMobile = styled(DateInfo)`
  position: static;
  p {
    text-align: left;
  }
`;
export const Info = styled.div`
  p {
    margin: 0;
    opacity: 0.6;
  }
`;

export function RfqDateInfo({ date }: { date: Date }) {
  return (
    <DateContainer>
      <p>{hourFormat(date)}</p>
      <p>{shortDateFormat(date)}</p>
    </DateContainer>
  );
}

const DateContainer = styled(DateInfo)`
  position: absolute;
  top: 20px;
  right: 240px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    position: relative;
    top: auto;
    right: auto;

    p {
      text-align: left;
      opacity: 0.7;
      margin: 0;
      font-size: 0.75rem;
      line-height: 1rem;
    }
  }
`;
